import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import {
  Box,
  Container,
  FAQSection,
  FeatureGrid,
  Heading,
  Link,
  Image,
  GridHero,
  ImageWithPlaceholder,
  LogoSection,
  LucidHead,
  Text,
  VideoPlayer,
  CardCarousel,
  TemplateSection,
  reactHTMLParser,
  CtaWithSSO,
  MultipleImageHero,
} from '@lucid/components';
import { isDefined } from '@lucid/utils';
import { withImage } from '@lucid/hooks';

import type { Lucid } from 'types/types';

const ConsiderationOrganic: FC<PropsWithChildren<Lucid.Context.Page.Props>> = ({ data, pageContext }) => {
  const { shortCode, urlLangPrefix, localeId, pageType, nid, shouldHideSsr, metatags, product } = pageContext;
  const { directus } = data;
  const node = directus.node;
  const { nodeTranslations } = node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Consideration_Organic_Translations;
  const {
    card_carousel,
    hero_type,
    hero,
    hero_alt,
    logo_banner_section,
    featured_video,
    upper_feature_grid_sections,
    faq_section_heading,
    faqs,
    template_cards,
    template_section_heading,
    template_section_link,
    template_section_text,
    should_require_template_grid: shouldRequireTemplateGrid,
    integration_section_heading,
    integration_section_text,
    integration_section_image,
    integration_link,
    lower_feature_grid_heading,
    lower_feature_grid_text,
    lower_feature_grid_items,
    middle_section_heading,
    middle_section_text,
    about_us_heading,
    about_us_text,
    resources_section_heading,
    resources_section_text,
    resources_section_items,
  } = translatedData;

  const faqItems = (faqs?.map(faq => faq.faq_id) || []).filter(isDefined);
  const heroLinks = (hero_alt?.link_set?.map(ls => ls.links_id) || []).filter(isDefined);
  const logoList = logo_banner_section?.logo_section?.logo_list
    ?.map(logo => logo.external_logo_id?.name)
    .filter(isDefined) as string[];
  const lowerFeatureGridItems = lower_feature_grid_items?.map(fgi => fgi.feature_grid_item_id).filter(isDefined);
  const cardCarouselItems = card_carousel?.items?.map(card => card.card_carousel_item_id).filter(isDefined);
  const templateCards = template_cards?.map(card => card.template_card_id).filter(isDefined);
  const resourcesSectionItems = resources_section_items?.map(rsi => rsi.feature_grid_item_id).filter(isDefined);

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      {hero_type === 'dual_image' && hero && (
        <MultipleImageHero hasSSO mobileImage="leftImage" variant="light" {...hero} />
      )}
      {hero_type === 'single_image' && hero_alt && (
        <Box
          css={theme => ({
            paddingTop: theme.space.s0,
            [theme.breakpoints.bp900.min]: {
              padding: `${theme.space.s64}px 0`,
            },
            backgroundColor: theme.colors.productColor({
              co: theme.colors.blue[20],
              scale: theme.colors.indigo[20],
              spark: theme.colors.red[20],
            }),
          })}
        >
          <Container>
            <GridHero css={theme => ({ padding: `${theme.space.s48}px 0` })} variant="rightImage">
              <GridHero.Heading
                as="h1"
                css={theme => ({
                  color: theme.colors.productColor({
                    co: theme.colors.blue[80],
                    scale: theme.colors.indigo[80],
                    spark: theme.colors.red[80],
                  }),
                })}
                variant="hero3XL700"
              >
                {hero_alt.heading}
              </GridHero.Heading>
              <GridHero.Text
                css={theme => ({
                  color: theme.colors.productColor({
                    co: theme.colors.blue[80],
                    scale: theme.colors.indigo[80],
                    spark: theme.colors.red[80],
                  }),
                })}
                variant="labelS400"
              >
                {hero_alt.text || ''}
              </GridHero.Text>
              <GridHero.Image
                alt={hero_alt.image?.translations?.[0]?.alt || ''}
                aspectRatio="16:9"
                src={withImage(hero_alt.image)}
              />
              <CtaWithSSO
                data-hero-role="sso"
                linkText={heroLinks?.[0]?.default_link_text}
                linkUrl={heroLinks?.[0]?.final_url}
                ssoSeparatorColor="coolGrey.100"
              />
            </GridHero>
          </Container>
        </Box>
      )}
      {!!logoList?.length && (
        <Box
          css={theme => [
            {
              width: '100%',
              overflow: 'hidden',
              margin: `${theme.space.s16}px 0`,
            },
          ]}
        >
          <LogoSection height={40} logos={logoList} logosColor="coolGrey" variant="carousel" />
        </Box>
      )}
      {upper_feature_grid_sections && (
        <Container
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.s64,
            padding: `${theme.space.s64}px 0`,
            [theme.breakpoints.bp500.min]: {
              gap: theme.space.s40,
            },
          })}
        >
          {upper_feature_grid_sections.map(({ feature_grid_section_id }, index) => {
            if (!feature_grid_section_id) return null;
            const { heading: sectionHeading, text: sectionText, items } = feature_grid_section_id;

            if (!items) return null;

            return (
              <FeatureGrid
                key={`featured-grid-${feature_grid_section_id.id}`}
                items={items.map(({ feature_grid_item_id }) => feature_grid_item_id).filter(isDefined)}
                linkProps={{
                  variant: 'primaryAlternate',
                }}
                sectionSubtitle={sectionText}
                sectionTitle={sectionHeading}
                titleTextVariant="headingXL"
                variant={index % 2 ? 'alternatingReversed' : 'alternating'}
              />
            );
          })}
        </Container>
      )}
      {!!cardCarouselItems?.length && card_carousel?.heading && (
        <CardCarousel cardCarouselItems={cardCarouselItems} cardCarouselSectionHeading={card_carousel?.heading} />
      )}
      {featured_video?.video_set?.[0] && (
        <Box
          css={theme => ({
            backgroundColor: theme.colors.coolGrey[0],
            padding: `${theme.space.s40}px 0 ${theme.space.s64}px 0`,
          })}
        >
          <Container>
            <Box
              css={theme => ({
                textAlign: 'center',
                paddingBottom: theme.space.s24,
                maxWidth: '780px',
                margin: 'auto',
              })}
            >
              <Heading as="h2" variant="headingXL">
                {featured_video.heading}
              </Heading>
              <Text variant="paragraphM400">{featured_video.text}</Text>
            </Box>
            <Box
              css={theme => ({
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'center',
                [theme.breakpoints.bp1220.min]: {
                  justifyContent: 'space-between',
                },
              })}
            >
              <Box
                css={theme => ({
                  flex: '1 1 100%',
                  [theme.breakpoints.bp1220.min]: {
                    flex: '0 1 50%',
                  },
                })}
              >
                <VideoPlayer
                  controls
                  description={featured_video.video_set?.[0]?.video_id?.alt_text}
                  placeholder={() =>
                    featured_video.video_set![0]?.video_id?.preview_image ? (
                      <ImageWithPlaceholder
                        alt={`${featured_video.video_set![0]?.video_id?.preview_image.title}`}
                        src={withImage(featured_video.video_set![0]?.video_id?.preview_image)}
                      />
                    ) : null
                  }
                  playing={false}
                  url={featured_video?.video_set?.[0]?.video_id?.external_url}
                />
              </Box>
              <Box
                css={theme => ({
                  marginTop: theme.space.s32,
                  flex: '1 1 100%',
                  [theme.breakpoints.bp1220.min]: {
                    marginTop: 0,
                    flex: '0 1 40%',
                  },
                })}
              >
                {featured_video.link?.final_url && (
                  <>
                    {featured_video.html && (
                      <Box css={theme => [theme.shared.htmlParserStyles]} id="bullet-box">
                        {reactHTMLParser(featured_video.html)}
                      </Box>
                    )}
                    <Link displayAsButton css={{ width: 'auto' }} href={featured_video.link?.final_url}>
                      {featured_video.link?.default_link_text}
                    </Link>
                  </>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      )}
      <TemplateSection
        cards={templateCards}
        heading={template_section_heading}
        link={template_section_link}
        shouldUseGrid={shouldRequireTemplateGrid}
        text={template_section_text}
      />
      {integration_section_heading && integration_section_image && (
        <Box
          as="section"
          css={theme => ({
            backgroundColor: theme.colors.productColor({
              co: theme.colors.blue[10],
              scale: theme.colors.indigo[10],
              spark: theme.colors.red[10],
            }),
            padding: `${theme.space.s64}px 0`,
          })}
        >
          <Container>
            <Heading as="h2" css={{ textAlign: 'center' }} variant="heading2XL">
              {integration_section_heading}
            </Heading>
            {integration_section_text && (
              <Text css={{ textAlign: 'center' }} variant="paragraphM400">
                {integration_section_text}
              </Text>
            )}
            <Image
              css={theme => ({ paddingTop: theme.space.s80 })}
              imageAlt={integration_section_image.title || ''}
              imageSrc={withImage(integration_section_image)}
            />
            <Box css={{ display: 'flex', justifyContent: 'center' }}>
              <Link
                css={theme => ({ margin: 'auto', marginTop: theme.space.s40 })}
                href={integration_link?.final_url}
                iconAnimation="navigational"
                variant="primaryAlternate"
              >
                {integration_link?.default_link_text}
              </Link>
            </Box>
          </Container>
        </Box>
      )}
      {lowerFeatureGridItems && lowerFeatureGridItems.length && (
        <Container
          css={theme => ({
            padding: `${theme.space.s64}px 0`,
          })}
        >
          <FeatureGrid
            columns={4}
            items={lowerFeatureGridItems}
            sectionSubtitle={lower_feature_grid_text}
            sectionTitle={lower_feature_grid_heading}
            subtitleTextVariant="paragraphM400"
            titleTextVariant="headingXL"
          />
        </Container>
      )}
      {middle_section_heading && middle_section_text && (
        <Box
          css={theme => ({
            backgroundColor: theme.colors.coolGrey[5],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${theme.space.s64}px 0`,
          })}
        >
          <Container css={theme => ({ [theme.breakpoints.bp900.min]: { maxWidth: '750px', padding: 0 } })}>
            <Heading as="h2" css={{ textAlign: 'center' }} variant="headingXL">
              {middle_section_heading}
            </Heading>
            <Text css={{ textAlign: 'center' }} variant="paragraphM400">
              {middle_section_text}
            </Text>
          </Container>
        </Box>
      )}
      {about_us_heading && about_us_text && (
        <Container>
          <Box
            css={theme => ({
              margin: `${theme.space.s64}px 0`,
              padding: theme.space.s40,
              borderRadius: theme.radii.r24,
              backgroundColor: theme.colors.coolGrey[5],
            })}
          >
            <Box
              css={theme => ({
                padding: theme.space.s40,
                borderRadius: theme.radii.r24,
                backgroundColor: theme.colors.white,
              })}
            >
              <Heading as="h2" css={{ textAlign: 'center' }} variant="heading2XL">
                {about_us_heading}
              </Heading>
              <Text variant="paragraphS400">{about_us_text}</Text>
            </Box>
          </Box>
        </Container>
      )}
      {!!resourcesSectionItems?.length && (
        <Box
          as="section"
          css={theme => ({
            backgroundColor: theme.colors.productColor({
              co: theme.colors.blue[5],
              scale: theme.colors.indigo[5],
              spark: theme.colors.red[5],
            }),
            padding: `${theme.space.s80}px 0`,
          })}
        >
          <Container>
            <FeatureGrid
              items={resourcesSectionItems}
              sectionSubtitle={resources_section_text}
              sectionTitle={resources_section_heading}
              titleTextVariant="headingXL"
              variant="resources"
            />
          </Container>
        </Box>
      )}
      {faqs && faqItems.length && (
        <FAQSection
          hasDarkBackground
          extraPadding={3}
          items={faqItems}
          linkVariant="primaryInverse"
          sectionTitle={faq_section_heading}
        />
      )}
    </Layout>
  );
};

export default ConsiderationOrganic;

export const Head: HeadFC<Lucid.Context.Head.Data, Lucid.Context.Page.Base> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query ConsiderationOrganicQuery($nid: ID!, $shortCode: String!) {
    directus {
      node: consideration_organic_by_id(id: $nid) {
        nodeTranslations: translations {
          path {
            url
          }
          languages_code {
            short_code
          }
          is_published
        }
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          id
          hero_type
          hero {
            id
            heading
            text
            left_image {
              ...mediaFragment
            }
            left_overflow
            left_vertical_align
            left_vertical_offset
            right_image {
              ...mediaFragment
            }
            right_overflow
            right_vertical_align
            right_vertical_offset
            link_set {
              links_id {
                id
                default_link_text
                final_url
              }
            }
          }
          hero_alt {
            heading
            text
            image {
              ...mediaFragment
            }
            link_set {
              links_id {
                id
                beacon_id
                final_url
                default_link_text
              }
            }
          }
          logo_banner_section {
            logo_section {
              id
              logo_list {
                external_logo_id {
                  id
                  name
                }
              }
            }
          }
          upper_feature_grid_sections {
            feature_grid_section_id {
              id
              heading
              text
              items {
                feature_grid_item_id {
                  id
                  heading
                  html
                  link {
                    ...linkFragment
                  }
                  image {
                    ...mediaFragment
                  }
                }
              }
            }
          }
          card_carousel {
            heading
            items {
              card_carousel_item_id {
                id
                heading
                text
                bullet_points
                link {
                  ...linkFragment
                }
                media {
                  ...mediaFragment
                }
                integration_icons {
                  directus_files_id {
                    ...mediaFragment
                  }
                }
              }
            }
          }
          should_require_template_grid
          template_section_heading
          template_section_text
          template_section_link {
            default_link_text
            final_url
          }
          template_cards {
            template_card_id {
              id
              template_id
              heading
              image {
                ...mediaFragment
              }
              link {
                ...linkFragment
              }
            }
          }
          featured_video {
            id
            heading
            link {
              ...linkFragment
            }
            text
            html
            video_set {
              video_id {
                id
                external_url
                alt_text
                title
                preview_image {
                  ...mediaFragment
                }
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  title
                  alt_description
                }
              }
            }
          }
          integration_section_heading
          integration_section_text
          integration_section_image {
            ...mediaFragment
          }
          integration_link {
            default_link_text
            final_url
            beacon_id
          }
          lower_feature_grid_heading
          lower_feature_grid_text
          lower_feature_grid_items {
            feature_grid_item_id {
              heading
              html
              link {
                ...linkFragment
              }
              image {
                ...mediaFragment
              }
            }
          }
          middle_section_heading
          middle_section_text
          about_us_heading
          about_us_text
          resources_section_heading
          resources_section_text
          resources_section_items {
            feature_grid_item_id {
              id
              heading
              html
              link {
                ...linkFragment
              }
              image {
                ...mediaFragment
              }
            }
          }
          faq_section_heading
          faqs {
            faq_id {
              id
              question
              answer
              links {
                links_id {
                  id
                  default_link_text
                  final_url
                  beacon_id
                }
              }
            }
          }
        }
      }
    }
  }
`;
